import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import axios from "../../config/axios";
import { useAuth } from "./AuthContext";
import { useGeneral } from "./GeneralContext";
import { useAddress } from "./SubscriptionContext";

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const { isLoggedIn } = useAuth();
  const { renderCart } = useGeneral();
  // ✅ State
  const [cart, setCartData] = useState({
    customer: null,
    orderCart: null,
    mealCart: null,
    shippingCharges: [], // normalized array of { city, amount }
  });
  const [loading, setLoading] = useState(false);
  const [itemLoading, setItemLoading] = useState(null);
  const [selectedCity, setSelectedCity] = useState(
    localStorage.getItem("selectedCity") || ""
  );

  // ✅ Fetch cart
  const fetchCart = useCallback(async (showLoader = true) => {
    const cartId = localStorage.getItem("cartId");
    const mealCartId = localStorage.getItem("mealCartId");
    if (!cartId && !mealCartId) {
      setCartData({
        customer: null,
        orderCart: null,
        mealCart: null,
        shippingCharges: [],
      });
      if (showLoader) setLoading(false);
      return;
    }

    try {
      if (showLoader) setLoading(true);
      const res = await axios.post("/cart", {
        cartId: cartId === "null" ? "" : cartId,
        mealSubscriptionCartId:
          mealCartId === "null" || mealCartId === null ? "" : mealCartId,
      });

      const customer = res?.data?.customer || null;
      const orderCart = res?.data?.orderCart || null;
      const mealCart = res?.data?.mealCart || null;
      const orderShippingCharges = res?.data?.orderShippingCharges || [];

      // Normalize shipping charges for easy lookup
      const shippingCharges = orderShippingCharges.map((item) => {
        const city = item.name
          .replace("order_shipping_charge_", "")
          .split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ");
        return { city, amount: Number(item.value) };
      });

      setCartData({ customer, orderCart, mealCart, shippingCharges });
    } catch (err) {
      console.error("Failed to fetch cart:", err);
      setCartData({
        customer: null,
        orderCart: null,
        mealCart: null,
        shippingCharges: [],
      });
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);

  // ✅ Add / remove / update items (orderCart only)
  const addToCart = async (productId, quantity = 1) => {
    try {
      const cartId = localStorage.getItem("cartId");
      const res = await axios.post("/cart/add-product-to-cart", {
        cartId,
        productId,
        quantity,
      });
      if (res.data?.cartId) localStorage.setItem("cartId", res.data.cartId);
      await fetchCart(false);
    } catch (err) {
      console.error("Failed to add to cart:", err);
      throw err;
    }
  };

  const removeFromCart = async (index) => {
    const cartId = localStorage.getItem("cartId");
    const productId = cart.orderCart?.items?.[index]?.product?._id;
    if (!cartId || !productId) return;

    setItemLoading(index);
    try {
      await axios.post("/cart/remove-product-from-cart", { cartId, productId });
      await fetchCart(false);
    } catch (err) {
      console.error("Failed to remove item:", err);
    } finally {
      setItemLoading(null);
    }
  };

  const updateCartQuantity = async (index, action) => {
    const cartItemId = cart.orderCart?.items?.[index]?._id;
    if (!cartItemId) return;

    setItemLoading(index);
    try {
      await axios.post("/cart/update-product-quantity", { cartItemId, action });
      await fetchCart(false);
    } catch (err) {
      console.error("Failed to update cart quantity:", err);
    } finally {
      setItemLoading(null);
    }
  };

  const clearCart = async () => {
    const cartId = localStorage.getItem("cartId");
    if (!cartId) return;
    try {
      await axios.post("/cart/clear", { cartId });
      localStorage.removeItem("cartId");
      setCartData({
        customer: null,
        orderCart: null,
        mealCart: null,
        shippingCharges: [],
      });
    } catch (err) {
      console.error("Failed to clear cart:", err);
    }
  };

  // ✅ Update delivery charge based on city selection
  const updateDeliveryChargeByCity = (city) => {
    setSelectedCity(city);
    localStorage.setItem("selectedCity", city);

    const chargeObj = cart.shippingCharges.find(
      (c) => c.city?.toLowerCase() === city?.toLowerCase()
    );
    const deliveryAmount = chargeObj ? chargeObj.amount : 0;

    if (!cart.orderCart) return;
    const totalAmount =
      cart.orderCart.invoice?.subTotal +
      cart.orderCart.invoice?.vatAmount +
      deliveryAmount;
    setCartData((prev) => ({
      ...prev,
      orderCart: {
        ...prev.orderCart,
        invoice: {
          ...prev.orderCart.invoice,
          shippingCharges: deliveryAmount,
          totalAmount,
        },
      },
    }));
  };

  // Apply stored city selection after fetch
  // useEffect(() => {
  //   if (selectedCity && cart.shippingCharges.length > 0) {
  //     updateDeliveryChargeByCity(selectedCity || "");
  //   }
  // }, [cart?.orderCart?.shippingAddress, selectedCity]);
  useEffect(() => {
    if (cart.shippingCharges.length <= 0) {
      fetchCart();
    }
  }, []);
  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        itemLoading,
        selectedCity,
        fetchCart,
        setSelectedCity,
        addToCart,
        removeFromCart,
        updateCartQuantity,
        clearCart,
        updateDeliveryChargeByCity,
        isLoggedIn,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

// Hook
export const useCart = () => useContext(CartContext);
